import React, { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import withShipment from '../../withShipment';
import { userInfoSelector } from '../../redux/selectors/auth';
import {
    accountsSelector,
    trackerLoadedSelector,
} from '../../redux/selectors/businessTracker';
import {
    listTrackerAccounts,
    saveTrackerAccount,
    cancelTrackerAccount,
    deleteTrackerAccount,
    saveTrackerCell,
} from '../../redux/actions/businessTracker';
import { TRACKERS } from './config/trackers';
import { BELONGS_META } from './config/belongs';
import CustomToolbar from '../global/CustomToolbar';
import Dashboard from './Dashboard';
import TrackerDetail from './TrackerDetail';
import BelongsView from './BelongsView';
import AddAccountModal from './AddAccountModal';
import './BusinessTracker.css';

// Screen state machine. Only one screen is mounted at a time; chrome is global.
const SCREENS = {
    DASHBOARD: 'dashboard',
    TRACKER: 'tracker',
    BELONGS: 'belongs',
    ADD_ACCOUNT: 'addAccount',
};

function getLocationId(userInfo) {
    return userInfo?.company || userInfo?.companyId || null;
}

function BusinessTrackerBase({
    userInfo,
    accounts,
    loaded,
    listTrackerAccounts: listAction,
    saveTrackerAccount: saveAction,
    cancelTrackerAccount: cancelAction,
    deleteTrackerAccount: deleteAction,
    saveTrackerCell: saveCellAction,
}) {
    const locationId = useMemo(() => getLocationId(userInfo), [userInfo]);
    const [screen, setScreen] = useState(SCREENS.DASHBOARD);
    const [ctx, setCtx] = useState({
        trackerKey: null,
        belongsFilter: 'all',
        belongsKey: null,
        editingAccount: null,
    });
    const [toasts, setToasts] = useState([]);

    useEffect(() => {
        if (locationId) listAction({ locationId });
    }, [locationId, listAction]);

    // Toast: auto-expire each toast individually after 2.5s.
    useEffect(() => {
        if (toasts.length === 0) return undefined;
        const id = toasts[0].id;
        const timer = setTimeout(
            () => setToasts((prev) => prev.filter((t) => t.id !== id)),
            2500,
        );
        return () => clearTimeout(timer);
    }, [toasts]);

    const showToast = useCallback((message, kind = 'info') => {
        if (!message) return;
        setToasts((prev) => [
            ...prev,
            { id: Date.now() + Math.random(), message, kind },
        ]);
    }, []);

    const navTracker = useCallback((trackerKey, belongsFilter = 'all') => {
        setCtx({ trackerKey, belongsFilter, belongsKey: null, editingAccount: null });
        setScreen(SCREENS.TRACKER);
        if (typeof window !== 'undefined') window.scrollTo(0, 0);
    }, []);

    const navBelongs = useCallback((belongsKey) => {
        setCtx({ trackerKey: null, belongsFilter: 'all', belongsKey, editingAccount: null });
        setScreen(SCREENS.BELONGS);
        if (typeof window !== 'undefined') window.scrollTo(0, 0);
    }, []);

    const navDashboard = useCallback(() => {
        setCtx({ trackerKey: null, belongsFilter: 'all', belongsKey: null, editingAccount: null });
        setScreen(SCREENS.DASHBOARD);
        if (typeof window !== 'undefined') window.scrollTo(0, 0);
    }, []);

    // Navigate to the Add/Edit Account screen. Preserves the originating
    // tracker + belongs filter so the back arrow returns there.
    const navAddAccount = useCallback((trackerKey, editingAccount = null) => {
        setCtx((prev) => ({
            trackerKey,
            belongsFilter: prev.belongsFilter || 'all',
            belongsKey: null,
            editingAccount,
        }));
        setScreen(SCREENS.ADD_ACCOUNT);
        if (typeof window !== 'undefined') window.scrollTo(0, 0);
    }, []);

    // Back from Add/Edit returns to the originating tracker screen, keeping
    // the previous belongs filter so the user lands where they were.
    const navBackFromAddAccount = useCallback(() => {
        setScreen(SCREENS.TRACKER);
        setCtx((prev) => ({ ...prev, editingAccount: null }));
        if (typeof window !== 'undefined') window.scrollTo(0, 0);
    }, []);

    const api = useMemo(() => ({
        saveAccount: (payload, cb) => {
            saveAction({ locationId, ...payload }, (err, msg) => {
                if (!err) showToast(msg || 'Saved', 'success');
                else showToast(msg || 'Save failed', 'error');
                if (cb) cb(err, msg);
            });
        },
        cancelAccount: (accountId, cb) => {
            cancelAction({ locationId, accountId }, (err, msg) => {
                if (!err) showToast(msg || 'Cancelled', 'success');
                else showToast(msg || 'Cancel failed', 'error');
                if (cb) cb(err, msg);
            });
        },
        deleteAccount: (accountId, cb) => {
            deleteAction({ locationId, accountId }, (err, msg) => {
                if (!err) showToast(msg || 'Deleted', 'success');
                else showToast(msg || 'Delete failed', 'error');
                if (cb) cb(err, msg);
            });
        },
        saveCell: (payload, cb) => {
            saveCellAction({ locationId, ...payload }, (err, msg) => {
                if (!err) showToast(msg || 'Saved', 'success');
                else if (msg) showToast(msg, 'error');
                if (cb) cb(err, msg);
            });
        },
    }), [locationId, saveAction, cancelAction, deleteAction, saveCellAction, showToast]);

    // Toolbar title reflects the active screen. Back arrow is shown on
    // tracker / belongs / addAccount screens (CustomToolbar's creatingOrder mode).
    const isSubScreen = screen === SCREENS.TRACKER
        || screen === SCREENS.BELONGS
        || screen === SCREENS.ADD_ACCOUNT;
    const toolbarTitle = useMemo(() => {
        if (screen === SCREENS.DASHBOARD) return 'Business Trackers';
        if (screen === SCREENS.TRACKER) {
            const t = TRACKERS[ctx.trackerKey];
            return t ? `${t.label} Accounts` : 'Tracker';
        }
        if (screen === SCREENS.BELONGS) {
            const meta = BELONGS_META[ctx.belongsKey];
            return meta ? meta.label : 'Category';
        }
        if (screen === SCREENS.ADD_ACCOUNT) {
            const t = TRACKERS[ctx.trackerKey];
            const verb = ctx.editingAccount ? 'Edit' : 'Add';
            if (t?.addModalTitle && !ctx.editingAccount) return t.addModalTitle;
            // Keep this short — the BeeNexa toolbar slot is narrow on mobile
            // and a wrapping title bleeds over the page content.
            return t ? `${verb} ${t.label}` : `${verb} Account`;
        }
        return 'Business Trackers';
    }, [screen, ctx.trackerKey, ctx.belongsKey, ctx.editingAccount]);

    // Back from AddAccount returns to its originating tracker; the other
    // sub-screens go straight back to the dashboard.
    const handleToolbarBack = screen === SCREENS.ADD_ACCOUNT
        ? navBackFromAddAccount
        : navDashboard;
    const backTitle = screen === SCREENS.ADD_ACCOUNT
        ? 'Back'
        : 'Back to Dashboard';

    const renderToolbar = () => (
        <CustomToolbar
            title={toolbarTitle}
            creatingOrder={isSubScreen}
            backTitle={backTitle}
            handleClose={handleToolbarBack}
            customIcons={isSubScreen ? undefined : <Fragment />}
        />
    );

    if (!locationId) {
        return (
            <div className="bt-root">
                {renderToolbar()}
                <div className="bt-frame">
                    <div className="bt-empty">
                        Loading user context… If this persists, please re-login.
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="bt-root">
            {renderToolbar()}
            <div className="bt-frame">
                {screen === SCREENS.DASHBOARD && (
                    <Dashboard
                        accounts={accounts}
                        loaded={loaded}
                        onOpenTracker={navTracker}
                        onOpenBelongs={navBelongs}
                    />
                )}
                {screen === SCREENS.TRACKER && ctx.trackerKey && (
                    <TrackerDetail
                        trackerKey={ctx.trackerKey}
                        initialBelongsFilter={ctx.belongsFilter}
                        accounts={accounts}
                        api={api}
                        showToast={showToast}
                        onOpenAddAccount={navAddAccount}
                    />
                )}
                {screen === SCREENS.BELONGS && ctx.belongsKey && (
                    <BelongsView
                        belongsKey={ctx.belongsKey}
                        accounts={accounts}
                        onOpenTracker={navTracker}
                    />
                )}
                {screen === SCREENS.ADD_ACCOUNT && ctx.trackerKey && (
                    <AddAccountModal
                        tracker={TRACKERS[ctx.trackerKey]}
                        initial={ctx.editingAccount}
                        onClose={navBackFromAddAccount}
                        onSave={(payload) => {
                            api.saveAccount(payload, (err) => {
                                if (!err) navBackFromAddAccount();
                            });
                        }}
                        onSaveMany={(payloads) => {
                            let i = 0;
                            const next = () => {
                                if (i >= payloads.length) {
                                    navBackFromAddAccount();
                                    return;
                                }
                                const p = payloads[i];
                                i += 1;
                                api.saveAccount(p, (err) => {
                                    if (err) return;
                                    next();
                                });
                            };
                            next();
                        }}
                    />
                )}
            </div>
            <ToastContainer toasts={toasts} />
        </div>
    );
}

function ToastContainer({ toasts }) {
    if (!toasts.length) return null;
    return (
        <div className="bt-toast-container" role="status" aria-live="polite">
            {toasts.map((t) => (
                <div key={t.id} className={`bt-toast ${t.kind || 'info'}`}>
                    <span role="img" aria-label={t.kind || 'info'}>
                        {t.kind === 'success' ? '✓' : t.kind === 'error' ? '⚠' : 'ℹ'}
                    </span>
                    <span>{t.message}</span>
                </div>
            ))}
        </div>
    );
}

export default withShipment(
    {
        mapStateToProps: (state) => ({
            userInfo: userInfoSelector(state),
            accounts: accountsSelector(state),
            loaded: trackerLoadedSelector(state),
        }),
        actionCreators: {
            listTrackerAccounts,
            saveTrackerAccount,
            cancelTrackerAccount,
            deleteTrackerAccount,
            saveTrackerCell,
        },
    },
    BusinessTrackerBase,
);
